import type { LucideIcon } from "lucide-react";
import { InfoIcon } from "lucide-react";
import * as React from "react";

import {
  fieldErrorClassName,
  fieldLabelRowClassName,
  fieldOptionalMarkerClassName,
  fieldWrapperClassName,
} from "@/components/ui/form-field-styles";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";

/** Text field chrome shared by `Input` and inputs built on other primitives (`AutocompleteInput`). */
export const inputClassName =
  "border-input bg-input/20 file:text-foreground placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-ring/30 aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:bg-input/30 dark:aria-invalid:border-destructive/50 dark:aria-invalid:ring-destructive/40 h-7 w-full min-w-0 rounded-md border px-2 py-0.5 text-base/5 sm:text-xs/relaxed transition-colors outline-none file:inline-flex file:h-6 file:border-0 file:bg-transparent file:text-xs/relaxed file:font-medium focus-visible:ring-2 disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 aria-invalid:ring-2";

function Input({
  className,
  type,
  label,
  error,
  isOptional,
  leftIcon: LeftIcon,
  id: idProp,
  ...props
}: React.ComponentProps<"input"> & {
  label?: string;
  error?: string | false | null | undefined;
  isOptional?: boolean;
  leftIcon?: LucideIcon;
}) {
  const generatedId = React.useId();
  const id = idProp ?? generatedId;
  const input = (
    <div className="relative">
      {LeftIcon && (
        <LeftIcon
          aria-hidden="true"
          className="text-muted-foreground pointer-events-none absolute top-1/2 left-2 size-3.5 -translate-y-1/2"
        />
      )}
      <input
        id={id}
        type={type}
        data-slot="input"
        aria-invalid={Boolean(error) || props["aria-invalid"]}
        aria-describedby={error ? `${id}-error` : props["aria-describedby"]}
        className={cn(inputClassName, LeftIcon && "pl-7", className)}
        {...props}
      />
    </div>
  );

  if (!label && !error && !isOptional) {
    return input;
  }

  return (
    <div className={fieldWrapperClassName}>
      {label && (
        <div className={fieldLabelRowClassName}>
          <Label htmlFor={id}>{label}</Label>
          {isOptional && <span className={fieldOptionalMarkerClassName}>Opcional</span>}
        </div>
      )}
      {input}
      {error && (
        <div className={fieldErrorClassName} id={`${id}-error`}>
          <InfoIcon aria-hidden="true" className="size-3.5" />
          {error}
        </div>
      )}
    </div>
  );
}

export { Input };
